import React, { useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import * as API from 'api';
import { DateTime } from 'luxon';
import {
  TableContainer,
  CircularProgress,
  Paper,
  Box,
  Button,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  Tooltip,
} from '@material-ui/core';
import { ArrowLeft, ArrowRight } from '@material-ui/icons';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

import ServiceDealsTableRow from './service-deals-table-row';

const ServiceDeals = API.ServiceDeals();

const DEALS_PER_PAGE = 100;
const DATE_FMT = 'yyyy-MM-dd';

const renderDate = date => (date ? DateTime.fromISO(date).toFormat(DATE_FMT) : '-');

const noteStyles = {
  maxWidth: '200px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};
const columns = [
  {
    id: 'service_deal_id',
    label: 'System ID',
  },
  {
    id: 'ops_id',
    label: 'Ops ID',
  },
  {
    id: 'name',
    label: 'Deal Name',
  },
  {
    id: 'service_deal_type',
    label: 'Deal Type',
  },
  {
    id: 'user_group_name',
    label: 'Group',
  },
  {
    id: 'notes',
    label: 'Notes',
    render: notes => (
      <Tooltip title={notes}>
        <Box style={noteStyles}>{notes}</Box>
      </Tooltip>
    ),
  },
  {
    id: 'is_active',
    label: 'Active',
    render: isActive => (isActive ? 'yes' : 'no'),
  },
  {
    id: 'expiration_date',
    label: 'Expires At',
    render: renderDate,
  },
  {
    id: 'created_at',
    label: 'Created At',
    render: renderDate,
  },
  {
    id: 'updated_at',
    label: 'Updated At',
    render: renderDate,
  },
];
const ASC = 'asc';
const DSC = 'desc';

const ServiceDealsTableContainer = props => {
  const { activeOnly, search } = props;
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState(ASC);
  const [orderBy, setOrderBy] = useState('service_deal_id');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingServiceDealId, setDeletingServiceDealId] = useState(null);

  const getServiceDealsQuery = useQuery(['service-deals', page, search, activeOnly, order, orderBy], () => {
    return ServiceDeals.getServiceDeals({
      page,
      count: DEALS_PER_PAGE,
      search,
      is_active: activeOnly || null,
      // If activeOnly is false, don't filter using is_active at all.
      order_by: orderBy,
      order,
    });
  });
  const deals = getServiceDealsQuery.data?.results || [];
  const totalPages = Math.ceil(getServiceDealsQuery.data?.total / DEALS_PER_PAGE) || 0;

  const deleteServiceDealMutation = useMutation(serviceDealId => ServiceDeals.deleteServiceDeal(serviceDealId), {
    onSuccess: () => {
      setDeleteModalOpen(false);
      getServiceDealsQuery.refetch();
    },
    onError: error => {
      alert(error);
      deleteServiceDealMutation.reset();
    },
  });

  const handleRequestSort = id => {
    const isAsc = orderBy === id && order === ASC;
    setOrder(isAsc ? DSC : ASC);
    setOrderBy(id);
  };

  const deleteServiceDeal = () => deleteServiceDealMutation.mutateAsync(deletingServiceDealId);

  const pageSelect = (
    <Box>
      <Button disabled={page === 0} onClick={() => setPage(page - 1)}>
        <ArrowLeft />
      </Button>
      on page #{page + 1} of {totalPages}
      <Button disabled={page === totalPages - 1} onClick={() => setPage(page + 1)}>
        <ArrowRight />
      </Button>
    </Box>
  );

  const deleteModal = (
    <Dialog
      open={deleteModalOpen}
      onClose={() => setDeleteModalOpen(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description">
      <DialogTitle id="alert-dialog-title">Are you sure to delete service deal {deletingServiceDealId}?</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">This action cannot be undone.</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button disabled={deleteServiceDealMutation.isLoading} onClick={() => setDeleteModalOpen(false)}>
          No
        </Button>
        <Button disabled={deleteServiceDealMutation.isLoading} onClick={deleteServiceDeal} autoFocus>
          Yes
        </Button>
      </DialogActions>
    </Dialog>
  );

  const handleDeleteOnClick = id => {
    setDeletingServiceDealId(id);
    setDeleteModalOpen(true);
  };

  return (
    <TableContainer component={Paper}>
      {getServiceDealsQuery.isLoading && <CircularProgress />}
      {pageSelect}
      <Table>
        <TableHead>
          <TableRow>
            {columns.map(column => (
              <TableCell key={column.id} sortDirection={orderBy === column.id ? order : false}>
                <TableSortLabel
                  active={orderBy === column.id}
                  direction={orderBy === column.id ? order : ASC}
                  onClick={() => handleRequestSort(column.id)}>
                  {column.label}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {deals.map(deal => (
            <ServiceDealsTableRow
              key={deal.id}
              columns={columns}
              data={deal}
              handleDeleteOnClick={handleDeleteOnClick}
            />
          ))}
        </TableBody>
      </Table>
      {pageSelect}
      {deleteModal}
    </TableContainer>
  );
};

export default ServiceDealsTableContainer;
